
import { Check, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { QuoteForm, QuoteFormValues } from "./QuoteForm";
import PackagePriceCalculator from "./PackagePriceCalculator";
import PackageCalculatorWithQuote from "./PackageCalculatorWithQuote";

const Packages = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [packagePrices, setPackagePrices] = useState<{[key: number]: number}>({});
  
const { data: packages = [], isLoading, error } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const { data: packagesData, error: packagesError } = await supabase
        .from('sns_packages')
        .select(`
          *,
          pricing:sns_package_pricing(*),
          addons:sns_package_addons(*, sort_order),
          rooms:sns_room_packages(*)
        `)
        .eq('is_active', true)
        .order('id', { ascending: true });

      if (packagesError) {
        throw packagesError;
      }

      return packagesData || [];
    },
  });

  useEffect(() => {
    if (error) {
      console.error("Packages error:", error);
    }
  }, [error]);

  const handleQuoteSubmit = async (values: QuoteFormValues) => {
    console.log("Submitting quote request:", values);
    try {
      const { error } = await supabase
        .from('quote_requests')
        .insert([values]);

      if (error) throw error;
      
      console.log("Quote request submitted successfully");
      toast.success("Quote request submitted successfully!");
      setDialogOpen(false);
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Failed to submit quote request");
    }
  };

  const handlePriceChange = useCallback((packageId: number, price: number, details: any) => {
    setPackagePrices(prev => ({
      ...prev,
      [packageId]: price
    }));
  }, []);

  if (isLoading) {
    return (
      <section className="py-20">
        <div className="container">
          <h2 className="text-4xl font-bold text-center mb-12 text-shell-dark">Our Packages</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="p-8 glass-card rounded-lg animate-pulse"
              >
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-6"></div>
                <div className="space-y-3 mb-8">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
                  ))}
                </div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="packages-section" className="py-20">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4 text-shell-dark">Our Packages</h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Choose the perfect package for your next adventure. Customize your experience with our flexible pricing options.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full border border-gray-100"
            >
              {/* Package Image */}
              {pkg.image_url && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={pkg.image_url}
                    alt={pkg.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                <p className="text-gray-600 mb-4">{pkg.description}</p>
                
                
                
                {/* Price Calculator Component */}
                <div className="mb-4">
                  <PackagePriceCalculator
                    packageId={pkg.id}
                    packageName={pkg.name}
                    onPriceChange={(price, details) => handlePriceChange(pkg.id, price, details)}
                  />
                </div>

                {/* Pricing Display */}
                <div className="mb-6 flex-grow">
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-sm text-gray-500">Starting from</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-orange-500">{pkg.price}</span>
                    <span className="text-sm text-gray-500">/{pkg.duration}</span>
                  </div>
                  {packagePrices[pkg.id] && (
                    <div className="text-gray-700 font-medium">
                      Your price: ${packagePrices[pkg.id].toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3 mt-auto">
                  {/* Main Book Button */}
                  <Button 
                    className="w-full bg-travel-primary hover:bg-travel-primary/90 text-white font-semibold py-3"
                    onClick={() => {
                      const packagesElement = document.getElementById('packages-section');
                      if (packagesElement) {
                        packagesElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    Book This Package
                  </Button>
                  
                  {/* Secondary Actions */}
                  <div className="flex gap-2">
                    <PackageCalculatorWithQuote
                      packageId={pkg.id}
                      packageName={pkg.name}
                    />
                    
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline"
                          className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                          onClick={() => setSelectedPackage(pkg.name)}
                        >
                          Quick Quote
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white max-w-md">
                        <DialogHeader>
                          <DialogTitle>Request Quote for {pkg.name}</DialogTitle>
                        </DialogHeader>
                        <QuoteForm 
                          packageName={pkg.name} 
                          onSubmit={handleQuoteSubmit}
                          onClose={() => setDialogOpen(false)}
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Packages;
